import React, { useMemo } from "react";
import Header from "./Components/Header";
import SessionCard from "./Components/SessionCard";
import PageContainer from "./Components/PageContainer";
import MainContent from "./Components/MainContent";
import BackButton from "./Components/BackButton";
import SectionTitle from "./Components/SectionTitle";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { bookingService } from "../../services/booking.service";

const formatBookingDate = (value) => {
  if (!value) return "Date not provided";

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return String(value);

  return parsedDate.toLocaleDateString("en-AU", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const mapBookingToSessionCard = (booking, studentName) => ({
  id:
    booking?.id ||
    `${booking?.tutor?.id || booking?.tutor?.name || "tutor"}-${
      booking?.slot?.id || booking?.slot?.date || "slot"
    }`,
  tutorName: booking?.tutor?.name || "Tutor",
  image: booking?.tutor?.photoImage || "",
  date: formatBookingDate(booking?.slot?.date || booking?.slot?.start_time),
  time: booking?.slot?.time_label || "Time not provided",
  student: studentName || "Student",
  status: booking?.is_upcoming ? "Upcoming" : "Completed",
  personLabel: "Student",
});

const MyBookings = () => {
  const { user } = useSelector((state) => state.user);
  const displayName =
    user?.user_name ||
    user?.full_name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    "";
  const bookingsQuery = useQuery({
    queryKey: ["student-class-booking-list"],
    queryFn: bookingService.getBookings,
    retry: false,
  });

  const upcomingSessions = useMemo(
    () =>
      (bookingsQuery.data?.upcoming || []).map((booking) =>
        mapBookingToSessionCard(booking, displayName)
      ),
    [bookingsQuery.data?.upcoming, displayName]
  );
  const pastSessions = useMemo(
    () =>
      (bookingsQuery.data?.past || []).map((booking) =>
        mapBookingToSessionCard(booking, displayName)
      ),
    [bookingsQuery.data?.past, displayName]
  );

  return (
    <PageContainer>
      <div className="sticky top-0 z-50 bg-[#DFF2EB]/70 backdrop-blur-md">
        <Header />
      </div>

      <MainContent>
        <BackButton onClick={() => window.history.back()} label="Back to Dashboard" />
        
        <SectionTitle
          title="My Bookings"
          subtitle="Manage your tutoring sessions"
          className="mb-8"
        />

        <h3 className="text-lg font-semibold text-gray-800 mb-3">
          Upcoming Sessions ({upcomingSessions.length})
        </h3>

        <div className="flex flex-col gap-6 mb-10">
          {bookingsQuery.isError ? (
            <div className="rounded-3xl border border-red-200 bg-red-50 px-6 py-5 text-red-700">
              {bookingsQuery.error?.message || "Unable to load bookings right now."}
            </div>
          ) : bookingsQuery.isLoading ? (
            <div className="rounded-3xl border border-gray-200 bg-white px-6 py-5 text-gray-600">
              Loading bookings...
            </div>
          ) : upcomingSessions.length ? (
            upcomingSessions.map((session) => (
              <SessionCard key={session.id} session={session} />
            ))
          ) : (
            <div className="rounded-3xl border border-gray-200 bg-white px-6 py-5 text-gray-600">
              No upcoming bookings found.
            </div>
          )}
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">
          Past Sessions ({pastSessions.length})
        </h3>

        <div className="flex flex-col gap-6">
          {bookingsQuery.isError ? null : bookingsQuery.isLoading ? (
            <div className="rounded-3xl border border-gray-200 bg-white px-6 py-5 text-gray-600">
              Loading bookings...
            </div>
          ) : pastSessions.length ? (
            pastSessions.map((session) => (
              <SessionCard key={session.id} session={session} />
            ))
          ) : (
            <div className="rounded-3xl border border-gray-200 bg-white px-6 py-5 text-gray-600">
              No past bookings found.
            </div>
          )}
        </div>
      </MainContent>
    </PageContainer>
  );
};

export default MyBookings;
